import { useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, UserRoundCheck } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { TableLink } from "components/BaseTableV3/TableLink";
import { MetaLeadsTableActions } from "routes/meta_leads/MetaLeadsTableActions";
import { getNextQueryParams } from "components/helpers";
import RuvixxDate from "components/RuvixxDate";
import useAuthorized from "hooks/useAuthorized";
import useMetaSyncInProgress, {
  META_SYNC_POLL_INTERVAL_MS,
} from "hooks/useMetaSyncInProgress";
import usePageTitle from "hooks/usePageTitle";
import MetaAdAccountService from "services/MetaAdAccount";
import MetaLeadService from "services/MetaLead";

const leadsQueryKey = accountId => ["meta-leads", accountId];

const AccountLeadsPage = () => {
  const { accountId } = useParams();
  const canRead = useAuthorized([ACL_RELATIONSHIPS.metaAdAccount.readLeads]);

  const {
    data: account,
    isFetched,
    isError,
  } = useQuery({
    queryKey: ["meta-ad-account", accountId],
    queryFn: () => MetaAdAccountService.get(accountId),
    enabled: canRead,
  });

  usePageTitle(account?.name || "Leads");

  const columns = useMemo(
    () => [
      {
        header: "ID",
        id: "ID",
        accessorKey: "id",
        size: 50,
        cell: ({ row }) => (
          <TableLink
            href={`/leads/meta/${accountId}/lead/${row.getValue("ID")}/overview`}
          >
            {row.getValue("ID")}
          </TableLink>
        ),
      },
      {
        id: "Name",
        accessorKey: "name",
        header: "Name",
        size: 180,
      },
      { id: "Email", accessorKey: "email", header: "Email", size: 220 },
      { id: "Phone", accessorKey: "phone", header: "Phone", size: 150 },
      {
        id: "Campaign",
        accessorKey: "campaign_name",
        header: "Campaign",
        size: 180,
      },
      { id: "Form", accessorKey: "form_name", header: "Form", size: 180 },
      {
        id: "Created",
        accessorKey: "created_time",
        header: "Created",
        size: 170,
        enableSorting: true,
        cell: ({ row }) => {
          const date = row.getValue("Created");
          return date ? <RuvixxDate date={date} /> : null;
        },
      },
      {
        id: "Contact",
        accessorKey: "contact_id",
        header: "Contact",
        size: 150,
        enableSorting: false,
        cell: ({ row }) => {
          const { contact_id } = row.original;
          return contact_id ? (
            <Link
              to={`/contacts/${contact_id}`}
              // Semantic UI's stylesheet is unlayered, so its `a { color }`
              // beats the layered button utility and the link keeps its anchor
              // color. Force the button text color with `!` to override it.
              className={cn(
                buttonVariants({ variant: "outline", size: "sm" }),
                "tw:text-foreground!"
              )}
            >
              <UserRoundCheck data-icon="inline-start" />
              View Contact
            </Link>
          ) : (
            <span className="tw:text-[var(--color-text-muted)]">—</span>
          );
        },
      },
      {
        header: <div />,
        id: "Actions",
        accessorKey: "actions",
        size: 60,
        enableSorting: false,
        enableResizing: false,
        meta: { isDisplay: true },
        cell: ({ row }) => (
          <MetaLeadsTableActions
            original={row.original}
            accountId={accountId}
          />
        ),
      },
    ],
    [accountId]
  );
  const isSyncing = useMetaSyncInProgress(accountId, "live");

  const invalidateLeads = useCallback(
    () => queryClient.invalidateQueries({ queryKey: leadsQueryKey(accountId) }),
    [accountId]
  );

  const fetchLeads = useCallback(
    async queryParams => {
      const next = getNextQueryParams(queryParams, columns);
      return MetaLeadService.list(accountId, next);
    },
    [accountId, columns]
  );

  if (!canRead) {
    return (
      <div className="tw:w-full">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CircleAlert />
            </EmptyMedia>
            <EmptyTitle>Access denied</EmptyTitle>
            <EmptyDescription>
              You don&apos;t have permission to view these leads.
            </EmptyDescription>
          </EmptyHeader>
          <Link to="/">Back to home</Link>
        </Empty>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="tw:w-full">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CircleAlert />
            </EmptyMedia>
            <EmptyTitle>Couldn&apos;t load this account</EmptyTitle>
            <EmptyDescription>
              Something went wrong loading this Meta ad account. Please try
              again.
            </EmptyDescription>
          </EmptyHeader>
          <Link to="/">Back to home</Link>
        </Empty>
      </div>
    );
  }

  if (isFetched && !account) {
    return (
      <div className="tw:w-full">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CircleAlert />
            </EmptyMedia>
            <EmptyTitle>Account not found</EmptyTitle>
            <EmptyDescription>
              We couldn&apos;t find that Meta ad account.
            </EmptyDescription>
          </EmptyHeader>
          <Link to="/">Back to home</Link>
        </Empty>
      </div>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:min-w-0">
      <BaseTableV3
        columns={columns}
        header={account?.name || "Leads"}
        tableName="meta_leads"
        queryKey={leadsQueryKey(accountId)}
        fetchData={fetchLeads}
        invalidateData={invalidateLeads}
        refetchInterval={isSyncing ? META_SYNC_POLL_INTERVAL_MS : undefined}
        enableSearch
        enableSettings
      />
    </div>
  );
};

export default AccountLeadsPage;
